/* Arca Lucia: shared, category-aware filter presentation for all collection templates. */
(() => {
  if (window.arcaFacetsInitialized) return;
  window.arcaFacetsInitialized = true;

  const ensureGlobalFilterStyles = () => {
    if (document.getElementById('arca-global-filter-ui')) return;
    const style = document.createElement('style');
    style.id = 'arca-global-filter-ui';
    style.textContent = `
      @media screen and (max-width: 989px) {
        .facets-container .mobile-facets__open-wrapper { display:block; width:100%; }
        .facets-container .mobile-facets__open,
        .facets-container .mobile-facets__open:hover {
          box-sizing:border-box; min-height:5.2rem; width:100%; padding:1.2rem 1.4rem;
          border:1px solid rgba(var(--color-foreground),.18); background:rgb(var(--color-background));
          color:rgb(var(--color-foreground)) !important; -webkit-text-fill-color:rgb(var(--color-foreground)) !important;
          text-decoration:none; justify-content:flex-start; gap:.8rem;
        }
        .facets-container .mobile-facets__open-label {
          display:inline-block !important; color:rgb(var(--color-foreground)) !important;
          -webkit-text-fill-color:rgb(var(--color-foreground)) !important; opacity:1 !important;
          visibility:visible !important; font-size:1.35rem; font-weight:500; letter-spacing:.02em;
          line-height:1.2; white-space:nowrap;
        }
        .facets-container .mobile-facets__open > * + * { margin-left:0; }
        .facets-container .mobile-facets__open svg { width:1.9rem; height:1.9rem; margin:0; flex:0 0 1.9rem; }
        .facets-container .mobile-facets__open line,
        .facets-container .mobile-facets__open circle,
        .facets-container .mobile-facets__open:hover line,
        .facets-container .mobile-facets__open:hover circle { stroke:rgb(var(--color-foreground)) !important; }
      }
      .arca-designer-fallback__list { margin:0; padding:0; max-height:42rem; overflow:auto; }
      .arca-designer-filter-link { display:block; padding:.75rem 0; color:rgb(var(--color-foreground)); text-decoration:none; font-size:1.35rem; line-height:1.35; }
      .arca-designer-filter-link:hover { text-decoration:underline; text-underline-offset:.25rem; }
      .arca-designer-filter-link--mobile { padding:1.35rem 2.6rem; font-size:1.5rem; border-bottom:1px solid rgba(var(--color-foreground),.08); }
    `;
    document.head.appendChild(style);
  };
  ensureGlobalFilterStyles();

  const normalize = (value) => (value || '').toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  const collectionHandle = () => {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  };

  const specialSizeType = (name) => {
    const key = normalize(name);
    if (key.includes('waist') || key.includes('denim')) return 'denim';
    if (key.includes('shoe')) return 'shoe';
    if (key.includes('swimwear') || key.includes('sleepwear')) return 'swimwear';
    if (key.includes('belt')) return 'belt';
    if (key.includes('bracelet')) return 'bracelet';
    if (key.includes('necklace')) return 'necklace';
    if (/size|große|grosse|groesse|taglia/.test(key)) return 'generic';
    return '';
  };

  const hasActiveValue = (details) => Boolean(
    details.querySelector('input[type="checkbox"]:checked, input[type="number"][value]:not([value=""])')
  );

  const shouldHideForCollection = (details) => {
    if (hasActiveValue(details)) return false;
    const input = details.querySelector('input[name^="filter."]');
    const name = input?.name || '';
    const kind = specialSizeType(name);
    if (!kind) return false;

    const handle = collectionHandle();
    if (!handle) return false;

    const shoeHandles = new Set(['alle-schuhe', 'sneakers', 'sandalen', 'stiefel-stiefeletten', 'pumps', 'designer-schuhe-400-600']);
    const bagHandles = new Set(['alle-taschen', 'tragetaschen', 'clutch-taschen', 'taschen', 'taschen-accessoires', 'designer-taschen-unter-300', 'designer-taschen-300-500', 'designer-taschen-500-1-000', 'designer-taschen-uber-1-000']);
    const accessoryHandles = new Set(['accessoires', 'sonnenbrillen', 'schmuck', 'gurtel', 'schals']);

    if (kind === 'denim') return !['hosen', 'jeans'].includes(handle);
    if (kind === 'shoe') return !shoeHandles.has(handle);
    if (kind === 'swimwear') return handle !== 'bademode';
    if (kind === 'belt') return handle !== 'gurtel';
    if (kind === 'bracelet') return handle !== 'schmuck';
    if (kind === 'necklace') return true;
    if (kind === 'generic' && (shoeHandles.has(handle) || bagHandles.has(handle) || accessoryHandles.has(handle))) return true;

    return false;
  };

  const filterValues = (input) => {
    const root = input.closest('.parent-display, .mobile-facets__submenu');
    if (!root) return;
    const query = normalize(input.value);
    const items = Array.from(root.querySelectorAll('.no-js-hidden > .facets__item, .mobile-facets__list > .mobile-facets__item'));
    let matches = 0;
    items.forEach((item) => {
      const checkbox = item.querySelector('input[type="checkbox"]');
      const available = !checkbox || !checkbox.disabled || checkbox.checked;
      const label = item.querySelector('label span[aria-hidden="true"]') || item.querySelector('label');
      const match = available && (!query || normalize(label?.textContent || item.textContent).includes(query));
      item.hidden = !match;
      item.classList.toggle('hidden', !match || (!query && item.classList.contains('show-more-item') && !checkbox?.checked));
      if (match) matches += 1;
    });
    const showMore = root.querySelector('show-more-button');
    if (showMore) showMore.hidden = Boolean(query);
    const status = input.parentElement.querySelector('[data-arca-facet-status]');
    if (status) status.textContent = query ? String(matches) : '';
    let empty = root.querySelector('.arca-facet-no-results');
    if (query && matches === 0) {
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'arca-facet-no-results';
        empty.setAttribute('role', 'status');
        const locale = document.documentElement.lang;
        empty.textContent = locale.startsWith('it') ? 'Nessun risultato' : locale.startsWith('en') ? 'No results' : 'Keine Treffer';
        const list = root.querySelector('.facets-wrap, .mobile-facets__list');
        list?.insertAdjacentElement('afterend', empty);
      }
      empty.hidden = false;
    } else if (empty) empty.hidden = true;
  };

  const rank = (details) => {
    const name = details.querySelector('input[name^="filter."]')?.name.toLowerCase() || '';
    if (name === 'filter.p.vendor') return 0;
    if (specialSizeType(name)) return 1;
    if (/color|colour|farbe/.test(name)) return 2;
    if (name.includes('filter.v.price.')) return 4;
    if (name === 'filter.v.availability') return 5;
    return 3;
  };

  const enhance = () => {
    ['#FacetsWrapperDesktop', '.mobile-facets__main'].forEach((selector) => {
      const container = document.querySelector(selector);
      if (!container) return;
      const groups = Array.from(container.querySelectorAll(':scope > details.js-filter'));
      groups.forEach((group) => {
        group.hidden = shouldHideForCollection(group);
      });
      const visibleGroups = groups.filter((group) => !group.hidden);
      const ordered = [...visibleGroups].sort((a, b) => rank(a) - rank(b));
      if (ordered.some((group, index) => group !== visibleGroups[index])) {
        const markers = visibleGroups.map((group) => {
          const marker = document.createComment('filter');
          group.before(marker);
          return marker;
        });
        ordered.forEach((group, index) => markers[index].replaceWith(group));
      }
    });
    document.querySelectorAll('[data-arca-facet-search]').forEach((input) => {
      if (input.value) filterValues(input);
    });

    document.querySelectorAll('input[name="filter.p.vendor"]').forEach((input) => {
      const scope = input.closest('details.js-filter');
      if (!scope) return;

      scope.querySelectorAll('.show-more-item').forEach((item) => {
        item.classList.remove('show-more-item', 'hidden');
        item.hidden = false;
      });

      const showMore = scope.querySelector('show-more-button');
      if (showMore) showMore.hidden = true;
    });
  };

  const filterDesignerMenu = (input) => {
    const root = input.closest('.parent-display, .mobile-facets__submenu');
    if (!root) return;
    const query = normalize(input.value);
    root.querySelectorAll('[data-arca-designer-menu-item]').forEach((item) => {
      item.hidden = Boolean(query) && !normalize(item.textContent).includes(query);
    });
  };

  ['input', 'search'].forEach((name) => document.addEventListener(name, (event) => {
    if (event.target.matches('[data-arca-facet-search]')) filterValues(event.target);
    if (event.target.matches('[data-arca-designer-menu-search]')) filterDesignerMenu(event.target);
  }));
  document.addEventListener('arca:facets-updated', enhance);
  document.addEventListener('shopify:section:load', enhance);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', enhance, { once: true });
  else enhance();
})();
